// ============================================================================
// Wave 3.26 — Comment callout badge + inline filtered comment tile reveal.
//
// Used in multiple Order Detail sections (Ghat, Requirement row, RML material
// header, RML packet group) to surface the count of comments tagged to that
// scope and let the user expand an inline filtered list on click.
// ============================================================================

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, X, Shield, CheckCircle2 } from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { useOrderComments } from './OrderCommentsContext';

/**
 * <CommentCalloutBadge>
 * Compact badge button. When count == 0 the badge is rendered as a faint
 * "0" pill (helps users discover the feature) but stays subtle. When count
 * > 0 it highlights and is clickable to toggle inline reveal.
 *
 * Props:
 *  - filterKey: stable key used to track open state across rerenders
 *  - spec: filter spec passed to matchComment (topic/subtopic/sub-subtopic)
 *  - label: optional aria-label / tooltip text (e.g. "View comments on this packet")
 *  - testId
 *  - tone: 'navy' (default) | 'amber' | 'rose' | 'emerald' — visual accent
 *  - composerPreset: optional { topic, subtopicId } — when provided, OPENING
 *    the callout also silently pre-scopes the main composer to that scope
 *    (no scroll), so the operator can jump straight to posting there.
 */
export function CommentCalloutBadge({ filterKey, spec, label, testId, tone = 'navy', size = 'sm', composerPreset }) {
  const { countFor, isOpen, toggleFilter, applyComposerPreset } = useOrderComments();
  const n = countFor(spec);
  const open = isOpen(filterKey);

  const toneCls = {
    navy: 'bg-slate-100 text-[var(--ink-navy)] hover:bg-slate-200',
    amber: 'bg-amber-50 text-amber-800 hover:bg-amber-100',
    rose: 'bg-rose-50 text-rose-700 hover:bg-rose-100',
    emerald: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  }[tone] || 'bg-slate-100 text-[var(--ink-navy)] hover:bg-slate-200';

  const sizeCls = size === 'xs' ? 'text-[10px] h-5 px-1.5' : 'text-[11px] h-6 px-2';

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        const willOpen = !isOpen(filterKey);
        toggleFilter(filterKey, spec);
        if (willOpen && composerPreset) {
          applyComposerPreset(composerPreset, { scroll: false });
        }
      }}
      title={label || `View comments${n > 0 ? ` (${n})` : ''}`}
      aria-label={label || `Comments callout: ${n}`}
      data-testid={testId}
      data-callout-open={open ? '1' : '0'}
      className={`inline-flex items-center gap-1 rounded-md font-medium transition-colors ${toneCls} ${sizeCls} ${open ? 'ring-2 ring-offset-1 ring-[var(--gold-icon)]/50' : ''} ${n === 0 ? 'opacity-50' : ''}`}
    >
      <MessageSquare size={11} />
      <span>{n}</span>
    </button>
  );
}

/**
 * <InlineCommentList>
 * Renders the filtered comment tiles when the badge is open. Should be
 * placed directly under the section the badge belongs to. When ``composerPreset``
 * is provided it shows an "Add Comment" button that pre-scopes the main
 * composer to that scope and scrolls/focuses it.
 */
export function InlineCommentList({ filterKey, spec, emptyHint = 'No comments under this scope yet.', testIdPrefix = 'inline-comment', composerPreset }) {
  const { filter, isOpen, closeFilter, applyComposerPreset } = useOrderComments();
  if (!isOpen(filterKey)) return null;
  const items = filter(spec);

  return (
    <div
      className="mt-2 rounded-md border border-[var(--divider)] bg-slate-50/60 p-2 space-y-1.5"
      data-testid={`${testIdPrefix}-panel-${filterKey}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-medium text-slate-600 uppercase tracking-wide">
          Comments ({items.length})
        </div>
        <div className="flex items-center gap-2">
          {composerPreset && (
            <button
              type="button"
              onClick={() => applyComposerPreset(composerPreset)}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white text-[10px] font-medium px-2 py-0.5 hover:opacity-90 transition-opacity"
              data-testid={`${testIdPrefix}-add-${filterKey}`}
              title="Post a new comment in this scope"
            >
              <MessageSquare size={10} />
              Add Comment
            </button>
          )}
          <button
            type="button"
            onClick={() => closeFilter(filterKey)}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close comments callout"
            data-testid={`${testIdPrefix}-close-${filterKey}`}
          >
            <X size={12} />
          </button>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] italic text-slate-500 py-2 text-center">{emptyHint}</div>
      ) : (
        items.map((c) => <CommentTileCompact key={c.id} c={c} testIdPrefix={testIdPrefix} />)
      )}
    </div>
  );
}

function CommentTileCompact({ c, testIdPrefix }) {
  const isCA = c.created_by_role === 'company_admin';
  const isSA = c.created_by_role === 'super_admin';
  const initials = (c.created_by_name || c.created_by_email || '?').slice(0, 2).toUpperCase();
  const saAcks = c.sa_acknowledged_by || [];
  return (
    <div
      id={`comment-${c.id}`}
      data-testid={`${testIdPrefix}-tile-${c.id}`}
      className="flex gap-2 p-2 rounded border border-[var(--divider)] bg-white"
    >
      <div className={`h-7 w-7 rounded-full grid place-items-center text-[10px] font-medium shrink-0 ${isSA ? 'bg-violet-100 text-violet-700' : isCA ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
          <span className="font-medium text-[var(--ink-navy)]">{c.created_by_name || c.created_by_email}</span>
          {isSA && <Badge className="bg-violet-50 text-violet-700 text-[9px] py-0 px-1"><Shield size={8} className="mr-0.5" />SA</Badge>}
          {isCA && <Badge className="bg-blue-50 text-blue-700 text-[9px] py-0 px-1">CA</Badge>}
          <span className="font-mono text-[9px] bg-slate-100 text-slate-600 px-1 rounded">{c.comment_no}</span>
          <span className="text-slate-400 text-[9px]">{formatDateTime(c.created_at)}</span>
          {c.requires_super_admin_attention && (
            saAcks.length > 0
              ? <Badge className="bg-emerald-50 text-emerald-700 text-[9px] py-0 px-1"><CheckCircle2 size={8} className="mr-0.5" />SA ack</Badge>
              : <Badge className="bg-amber-50 text-amber-700 text-[9px] py-0 px-1"><Shield size={8} className="mr-0.5" />SA pend</Badge>
          )}
        </div>
        {/* Wave 3.67 — Elevate the ghat sub-milestone into a colored badge
            so the section-level Milestones feed clearly labels each tile
            with the specific sub-stage (CAD / CAM / Ghat / Overall). */}
        {c.topic === 'ghat' && (c.subtopic_id || c.subtopic_label) && (() => {
          const sid = (c.subtopic_id || 'overall').toLowerCase();
          const label =
            sid === 'cad' ? 'CAD' :
            sid === 'cam' ? 'CAM' :
            sid === 'chamka' ? 'Chamka' :
            sid === 'ghat' ? 'Ghat' :
            'Overall';
          const tone =
            sid === 'cad' ? 'bg-sky-50 text-sky-700 border-sky-200' :
            sid === 'cam' ? 'bg-purple-50 text-purple-700 border-purple-200' :
            // Wave 3.73 — Chamka gets its own amber tone so it's visually
            // distinct from both CAM (purple) and Ghat (emerald).
            sid === 'chamka' ? 'bg-amber-50 text-amber-700 border-amber-200' :
            sid === 'ghat' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
            'bg-slate-50 text-slate-600 border-slate-200';
          return (
            <div className="mt-1">
              <Badge className={`text-[9px] uppercase tracking-wide border ${tone} px-1.5 py-0`}>
                Milestone · {label}
              </Badge>
            </div>
          );
        })()}
        {(c.sub_subtopic_label || (c.subtopic_label && c.topic !== 'ghat')) && (
          <div className="text-[9px] text-slate-500 mt-0.5">
            {c.subtopic_label}
            {c.sub_subtopic_label ? ` › ${c.sub_subtopic_label}` : ''}
          </div>
        )}
        <div className="text-[12px] text-slate-700 mt-1 whitespace-pre-wrap break-words" data-testid={`${testIdPrefix}-tile-body-${c.id}`}>{c.body}</div>
      </div>
    </div>
  );
}
